import * as XLSX from 'xlsx';

/**
 * Convert Excel serial date to YYYY-MM-DD
 */
function excelSerialToDate(serial) {
  // Excel dates are days since 1/1/1900 (with a bug for 1900 being a leap year)
  const utc = (serial - 25569) * 86400 * 1000;
  const date = new Date(utc);

  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

/**
 * Convert DD/MM/YYYY or DD/MM/YY to YYYY-MM-DD
 * Also handles Excel serial numbers
 */
export function convertDate(dateValue) {
  if (!dateValue) return null;

  // Handle Excel serial numbers (numeric dates)
  if (typeof dateValue === 'number') {
    return excelSerialToDate(dateValue);
  }

  if (typeof dateValue !== 'string') return null;
  const trimmed = dateValue.trim();
  if (!trimmed) return null;

  // Check if it's already in YYYY-MM-DD format
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    return trimmed;
  }

  const parts = trimmed.split('/');
  if (parts.length !== 3) return null;

  let [day, month, year] = parts;

  // Handle 2-digit years: convert YY to YYYY
  // Assume years 00-49 are 2000-2049, and 50-99 are 1950-1999
  if (year.length === 2) {
    const yearNum = parseInt(year, 10);
    year = yearNum < 50 ? `20${year}` : `19${year}`;
  }

  // Handle 4-digit years
  if (year.length === 4) {
    // Check if this is actually MM/DD/YYYY format (month > 12 means it's DD/MM/YYYY)
    const monthNum = parseInt(month, 10);

    // If day > 12 and month <= 12, it's definitely DD/MM/YYYY
    // If month > 12, it must be MM/DD/YYYY (swap them)
    if (monthNum > 12) {
      [day, month] = [month, day];
    }
  }

  const paddedDay = day.padStart(2, '0');
  const paddedMonth = month.padStart(2, '0');

  return `${year}-${paddedMonth}-${paddedDay}`;
}

/**
 * Clean number: remove $, commas, and convert to number
 */
export function cleanNumber(value) {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number') return value;

  const cleaned = value.toString().replace(/[$,]/g, '').trim();
  if (cleaned === '' || cleaned === 'N/A' || cleaned === '-') return null;

  const parsed = parseFloat(cleaned);
  return Number.isNaN(parsed) ? null : parsed;
}

/**
 * Parse Basic Valuation Inputs (lines 2-11)
 */
function parseBasicInputs(rows) {
  const basicInputs = {};

  // Line 2: Subject Company ID
  if (rows[1] && rows[1][1]) {
    basicInputs.subjectCompanyId = rows[1][1].toString().trim();
  }

  // Line 3: Valuation Date
  if (rows[2] && rows[2][1]) {
    basicInputs.valuationDate = convertDate(rows[2][1]);
  }

  // Line 4: Transaction Date
  if (rows[3] && rows[3][1]) {
    basicInputs.transactionDate = convertDate(rows[3][1]);
  }

  // Line 5: Cap Table Date (optional; default to valuation date when blank)
  if (rows[4] && rows[4][1]) {
    basicInputs.capTableDate = convertDate(rows[4][1]);
  } else if (basicInputs.valuationDate) {
    basicInputs.capTableDate = basicInputs.valuationDate;
  }

  // Line 6: Last Financial Statement Date
  if (rows[5] && rows[5][1]) {
    basicInputs.lastFinancialStatementDate = convertDate(rows[5][1]);
  }

  // Line 7: Fiscal Year End
  if (rows[6] && rows[6][1]) {
    basicInputs.fiscalYearEnd = convertDate(rows[6][1]);
  }

  // Line 10: Term (manual input)
  if (rows[9] && rows[9][1]) {
    basicInputs.term = cleanNumber(rows[9][1]);
  }

  // Line 11: Volatility (manual input)
  if (rows[10] && rows[10][1]) {
    basicInputs.volatility = cleanNumber(rows[10][1]);
  }

  return basicInputs;
}

/**
 * Parse Capital Structure (lines 14-77)
 */
/* eslint-disable no-plusplus, no-continue */
function parseCapStructure(rows) {
  const capStructure = {
    preferred: [],
    common: [],
    warrants: [],
    options: [],
    optionPool: null,
  };

  console.log('=== Starting Capital Structure Parsing ===');

  // Preferred shares (lines 15-34, Series 1-20)
  for (let i = 14; i <= 33; i++) {
    if (!rows[i]) continue;

    const seriesName = rows[i][0] ? rows[i][0].toString().trim() : '';
    const shares = rows[i][2] ? rows[i][2].toString().trim() : '';

    console.log(`Row ${i + 1} Preferred: seriesName="${seriesName}", shares="${shares}"`);

    // Skip if no shares (empty row)
    if (!shares || !seriesName.toLowerCase().includes('series')) continue;

    console.log(`  ✓ Adding preferred: ${seriesName} (${shares} shares)`);

    // Parse participation rights with default value
    let participationRights = rows[i][10] ? rows[i][10].toString().trim() : null;
    if (!participationRights) {
      participationRights = 'non-participating'; // Default to non-participating (lowercase to match dropdown)
    }

    capStructure.preferred.push({
      name: seriesName,
      transactionDate: rows[i][1] ? convertDate(rows[i][1]) : null,
      shares: cleanNumber(shares),
      mandatoryDividends: rows[i][3] && rows[i][3].toString().toUpperCase() === 'Y',
      dividendRate: cleanNumber(rows[i][4]),
      dividendCompounding: rows[i][5] ? rows[i][5].toString().trim() : null,
      dividendExpirationDate: rows[i][6] ? convertDate(rows[i][6]) : null,
      issuePrice: cleanNumber(rows[i][7]),
      liquidationPriority: cleanNumber(rows[i][8]),
      liquidationPreference: cleanNumber(rows[i][9]),
      participationRights,
      participationCap: rows[i][11] ? rows[i][11].toString().trim() : null,
      conversionRatio: cleanNumber(rows[i][12]),
    });
  }

  // Common shares (lines 37-41)
  // Line 36 is header "Common	Amount", data starts at line 37 (index 36)
  console.log('\nParsing Common shares...');
  for (let i = 36; i <= 40; i++) {
    if (!rows[i]) continue;

    // Get class name from column 0
    const className = rows[i][0] ? rows[i][0].toString().trim() : '';

    // Skip if no class name
    if (!className) continue;

    // Get amount from column 1
    const amount = rows[i][1] ? rows[i][1].toString().trim() : '';

    console.log(`Row ${i + 1} Common: className="${className}", amount="${amount}"`);

    // Skip if no amount (empty rows)
    if (!amount) continue;

    console.log(`  ✓ Adding common: ${className} (${amount} shares)`);

    capStructure.common.push({
      name: className,
      amount: cleanNumber(amount),
    });
  }

  console.log('\nCapital Structure Parsing Summary:');
  console.log(`  Preferred: ${capStructure.preferred.length} classes`);
  console.log(`  Common: ${capStructure.common.length} classes`);
  capStructure.preferred.forEach((p) => console.log(`    - ${p.name}`));
  capStructure.common.forEach((c) => console.log(`    - ${c.name}`));

  // Warrants (lines 44-68)
  // Series 1-20 warrants
  for (let i = 43; i <= 62; i++) {
    if (!rows[i]) continue;

    const seriesName = rows[i][0] ? rows[i][0].toString().trim() : '';
    const amount = rows[i][1] ? rows[i][1].toString().trim() : '';
    const exercisePrice = rows[i][2] ? rows[i][2].toString().trim() : '';

    // Skip if no amount
    if (!amount) continue;

    capStructure.warrants.push({
      name: seriesName,
      amount: cleanNumber(amount),
      exercisePrice: cleanNumber(exercisePrice),
    });
  }

  // Common/Class warrants (lines 64-68)
  // These come after Series warrants, starting at line 64 (index 63)
  for (let i = 63; i <= 67; i++) {
    if (!rows[i]) continue;

    // Get class name from column 0
    const className = rows[i][0] ? rows[i][0].toString().trim() : '';

    // Skip if no class name
    if (!className) continue;

    const amount = rows[i][1] ? rows[i][1].toString().trim() : '';
    const exercisePrice = rows[i][2] ? rows[i][2].toString().trim() : '';

    // Skip if no amount
    if (!amount) continue;

    capStructure.warrants.push({
      name: className,
      amount: cleanNumber(amount),
      exercisePrice: cleanNumber(exercisePrice),
    });
  }

  // Options (lines 71-75)
  for (let i = 70; i <= 74; i++) {
    if (!rows[i]) continue;

    // Get class name from column 0
    const className = rows[i][0] ? rows[i][0].toString().trim() : '';

    // Skip if no class name
    if (!className) continue;

    const amount = rows[i][1] ? rows[i][1].toString().trim() : '';
    const exercisePrice = rows[i][2] ? rows[i][2].toString().trim() : '';

    // Skip if no amount
    if (!amount) continue;

    capStructure.options.push({
      name: className,
      amount: cleanNumber(amount),
      exercisePrice: cleanNumber(exercisePrice),
    });
  }

  // Remaining Option Pool (line 77)
  if (rows[76] && rows[76][1]) {
    capStructure.optionPool = cleanNumber(rows[76][1]);
  }

  return capStructure;
}
/* eslint-enable no-plusplus, no-continue */

/**
 * Parse Fund Holdings (lines 81-144)
 * Dynamically reads class names from the Excel file
 */
/* eslint-disable no-plusplus, no-continue */
function parseHoldings(rows) {
  const holdings = [];
  const funds = ['Fund I, LP', 'Fund II, LP', 'Fund III, LP', 'Fund IV, LP', 'Fund V, LP'];
  const fundStartLines = [80, 93, 106, 119, 132]; // 0-indexed

  console.log('=== Starting Holdings Parsing ===');

  funds.forEach((fundName, fundIdx) => {
    const startLine = fundStartLines[fundIdx];
    console.log(`\nParsing fund: ${fundName}, starting at row ${startLine + 1}`);

    // Each fund section has up to 11 rows of data after the header
    // We'll read the actual class names from column 0 instead of hardcoding
    for (let i = 1; i <= 11; i++) {
      const rowIdx = startLine + i;
      if (!rows[rowIdx]) continue;

      // Read the actual class name from column 0
      const className = rows[rowIdx][0] ? rows[rowIdx][0].toString().trim() : '';
      const shares = rows[rowIdx][1] ? rows[rowIdx][1].toString().trim() : '';
      const price = rows[rowIdx][2] ? rows[rowIdx][2].toString().trim() : '';

      console.log(`  Row ${rowIdx + 1}: className="${className}", shares="${shares}", price="${price}"`);

      // Skip if no class name or no shares
      if (!className || !shares) continue;

      // Filter out any [Holdings] tags or empty values
      if (className.includes('[Holdings]')) continue;

      console.log(`  ✓ Adding holding: ${fundName} - ${className} (${shares} shares @ ${price})`);

      holdings.push({
        fundName,
        classOfStock: className,
        sharesOwned: cleanNumber(shares),
        pricePerShare: cleanNumber(price),
      });
    }
  });

  console.log('\n=== Holdings Parsing Complete ===');
  console.log('Total holdings parsed:', holdings.length);
  console.log('Holdings:', JSON.stringify(holdings, null, 2));

  return holdings;
}
/* eslint-enable no-plusplus, no-continue */

/**
 * Parse Financials (lines 147-163)
 * Dynamically finds the Financials section instead of relying on fixed line numbers
 */
function parseFinancials(rows) {
  const financials = {
    balanceSheet: {},
    incomeStatement: {},
  };

  console.log('=== Starting Financials Parsing ===');
  console.log('Total rows:', rows.length);

  // Find the Financials section header
  const financialsStartRowIndex = rows.findIndex((row, idx) => {
    if (row && row[0]) {
      const cellValue = row[0].toString().trim().toLowerCase();
      const isFinancials = cellValue === 'financials' || cellValue.includes('financials');
      if (isFinancials) {
        console.log(`Found Financials header at row ${idx}:`, row[0]);
      }
      return isFinancials;
    }
    return false;
  });

  console.log('Financials section found at row:', financialsStartRowIndex);

  // If we found the Financials header, parse from the next row
  const financialsStartRow = financialsStartRowIndex !== -1 ?
    financialsStartRowIndex :
    145; // Fallback to expected line numbers if header not found

  // Balance Sheet items start right after "Financials" header
  const balanceSheetStartRow = financialsStartRow + 1;
  const balanceSheetEndRow = Math.min(balanceSheetStartRow + 20, rows.length);

  console.log(`Parsing balance sheet from row ${balanceSheetStartRow} to ${balanceSheetEndRow}`);

  // Look for each balance sheet field by label
  rows.slice(balanceSheetStartRow, balanceSheetEndRow).forEach((row, idx) => {
    if (!row || !row[0]) {
      return;
    }

    const label = row[0].toString().trim().toLowerCase();
    const value = row[1];

    console.log(`Row ${balanceSheetStartRow + idx}: "${row[0]}" = "${value}"`);

    if (label.includes('total invested capital')) {
      financials.balanceSheet.totalInvestedCapital = cleanNumber(value);
      console.log('  -> Matched totalInvestedCapital:', financials.balanceSheet.totalInvestedCapital);
    } else if (label.includes('total debt') && !label.includes('non-convertible') && !label.includes('transaction date')) {
      financials.balanceSheet.totalDebt = cleanNumber(value);
      console.log('  -> Matched totalDebt:', financials.balanceSheet.totalDebt);
    } else if (label.includes('total non-convertible debt')) {
      financials.balanceSheet.totalNonConvertibleDebt = cleanNumber(value);
      console.log('  -> Matched totalNonConvertibleDebt:', financials.balanceSheet.totalNonConvertibleDebt);
    } else if (label.includes('debt as of transaction date')) {
      financials.balanceSheet.debtAsOfTransactionDate = cleanNumber(value);
      console.log('  -> Matched debtAsOfTransactionDate:', financials.balanceSheet.debtAsOfTransactionDate);
    } else if (label.includes('cash') && (label.includes('equivalents') || label.includes('& equivalents'))) {
      financials.balanceSheet.cashAndEquivalents = cleanNumber(value);
      console.log('  -> Matched cashAndEquivalents:', financials.balanceSheet.cashAndEquivalents);
    } else if (label.includes('cash runway')) {
      financials.balanceSheet.cashRunway = cleanNumber(value);
      console.log('  -> Matched cashRunway:', financials.balanceSheet.cashRunway);
    } else if (label.includes('$1m') || label.includes('$1 m') || (label.includes('revenue') && label.includes('million'))) {
      const boolValue = value ? value.toString().trim().toUpperCase() : '';
      financials.balanceSheet.revenueOverOneMillion = boolValue === 'Y' || boolValue === 'YES';
      console.log('  -> Matched revenueOverOneMillion:', financials.balanceSheet.revenueOverOneMillion);
    }
  });

  console.log('Balance Sheet parsed:', financials.balanceSheet);

  // Find Income Statement section
  const incomeStatementHeaderRowIndex = rows.findIndex((row, idx) => {
    if (row && row[0]) {
      const cellValue = row[0].toString().trim().toLowerCase();
      const isIncomeStatement = cellValue === 'income statement' || cellValue.includes('income statement');
      if (isIncomeStatement) {
        console.log(`Found Income Statement header at row ${idx}:`, row[0]);
      }
      return isIncomeStatement;
    }
    return false;
  });

  console.log('Income Statement section found at row:', incomeStatementHeaderRowIndex);

  if (incomeStatementHeaderRowIndex !== -1) {
    // Income Statement data starts right after header
    const periods = [
      { key: 'lfyMinus1', labels: ['lfy - 1', 'lfy-1', 'last fiscal year - 1'] },
      { key: 'lfy', labels: ['lfy', 'last fiscal year'] },
      { key: 'ltm', labels: ['ltm', 'last twelve months', 'last 12 months'] },
      { key: 'cfy', labels: ['cfy', 'current fiscal year'] },
      { key: 'ntm', labels: ['ntm', 'next twelve months', 'next 12 months'] },
      { key: 'nfy', labels: ['nfy', 'next fiscal year'] },
      { key: 'nfyPlus1', labels: ['nfy + 1', 'nfy+1', 'next fiscal year + 1'] },
      { key: 'nfyPlus2', labels: ['nfy + 2', 'nfy+2', 'next fiscal year + 2'] },
    ];

    // The header row contains "Income Statement", "Revenue", "Gross Profit", "EBITDA"
    // Data starts on the very next row
    const incomeStatementDataStart = incomeStatementHeaderRowIndex + 1;
    const incomeStatementEndRow = Math.min(incomeStatementDataStart + 15, rows.length);

    console.log(`Parsing income statement from row ${incomeStatementDataStart} to ${incomeStatementEndRow}`);

    // Parse income statement rows after the header
    rows.slice(incomeStatementDataStart, incomeStatementEndRow).forEach((row, idx) => {
      if (!row || !row[0]) {
        return;
      }

      const periodLabel = row[0].toString().trim().toLowerCase();

      console.log(`Income row ${incomeStatementDataStart + idx}: "${row[0]}" [${row[1]}, ${row[2]}, ${row[3]}]`);

      // Skip empty rows
      if (periodLabel === '') {
        return;
      }

      // Match period by label - prioritize exact matches
      let matchedPeriod = null;

      // First try exact match
      const exactMatchPeriod = periods.find((period) =>
        period.labels.some((l) => periodLabel === l));

      if (exactMatchPeriod) {
        matchedPeriod = exactMatchPeriod;
      } else {
        // If no exact match, try includes match (prefer longer matches)
        let bestMatch = null;
        let bestMatchLength = 0;

        periods.forEach((period) => {
          period.labels.forEach((label) => {
            if (periodLabel.includes(label) && label.length > bestMatchLength) {
              bestMatch = period;
              bestMatchLength = label.length;
            }
          });
        });

        matchedPeriod = bestMatch;
      }

      if (matchedPeriod) {
        // Revenue is typically in column 1, Gross Profit in 2, EBITDA in 3
        financials.incomeStatement[matchedPeriod.key] = {
          revenue: cleanNumber(row[1]),
          grossProfit: cleanNumber(row[2]),
          ebitda: cleanNumber(row[3]),
        };
        console.log(`  -> Matched ${matchedPeriod.key}:`, financials.incomeStatement[matchedPeriod.key]);
      } else {
        console.log(`  -> No match found for period: "${periodLabel}"`);
      }
    });
  }

  console.log('Final financials:', JSON.stringify(financials, null, 2));

  return financials;
}

/**
 * Parse Comps (lines 167-368)
 */
/* eslint-disable no-plusplus, no-continue */
function parseComps(rows) {
  const ciqIds = [];

  // Start from line 167 (index 166)
  for (let i = 166; i < rows.length; i += 1) {
    if (!rows[i] || !rows[i][0]) continue;

    const value = rows[i][0].toString().trim();

    // Skip if it's a tag or empty
    if (!value || value.includes('[Industry]')) continue;

    // Add CIQ ID
    ciqIds.push(value);
  }

  return { ciqIds };
}
/* eslint-enable no-plusplus, no-continue */

/**
 * Parse sections from rows
 */
function parseSections(rows) {
  return {
    basicInputs: parseBasicInputs(rows),
    capStructure: parseCapStructure(rows),
    holdings: parseHoldings(rows),
    financials: parseFinancials(rows),
    comps: parseComps(rows),
  };
}

/**
 * Validate parsed data
 */
export function validateParsedData(data) {
  const errors = [];
  const warnings = [];

  // Required fields
  if (!data.basicInputs.valuationDate) {
    errors.push('Valuation Date is required');
  }
  if (!data.basicInputs.transactionDate) {
    errors.push('Transaction Date is required');
  }
  if (!data.basicInputs.lastFinancialStatementDate) {
    errors.push('Last Financial Statement Date is required');
  }

  // Date logic validation
  if (data.basicInputs.transactionDate && data.basicInputs.valuationDate) {
    if (data.basicInputs.transactionDate > data.basicInputs.valuationDate) {
      errors.push('Transaction Date cannot be after Valuation Date');
    }
  }

  // Check if any data exists
  const hasCapStructure = data.capStructure.preferred.length > 0 ||
                          data.capStructure.common.length > 0;
  const hasHoldings = data.holdings.length > 0;
  const hasFinancials = Object.keys(data.financials.balanceSheet).length > 0 ||
                        Object.keys(data.financials.incomeStatement).length > 0;
  const hasComps = data.comps.ciqIds.length > 0;

  if (!hasCapStructure) {
    warnings.push('No capital structure data found');
  }
  if (!hasHoldings) {
    warnings.push('No holdings data found');
  }
  if (!hasFinancials) {
    warnings.push('No financial data found');
  }
  if (!hasComps) {
    warnings.push('No comps data found');
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
}

/**
 * Parse tab-delimited file
 */
export function parseCompanyDataFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = e.target.result;
        const workbook = XLSX.read(data, { type: 'binary', raw: true });

        // Get first sheet
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];

        // Convert to array of arrays (preserves tabs)
        // Use raw: true to get actual cell values (dates as numbers)
        const rows = XLSX.utils.sheet_to_json(firstSheet, {
          header: 1,
          defval: '',
          raw: true,
        });

        // Parse sections
        const parsedData = parseSections(rows);

        // Validate
        const validation = validateParsedData(parsedData);

        resolve({ data: parsedData, validation });
      } catch (error) {
        reject(new Error(`File parsing failed: ${error.message}`));
      }
    };

    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };

    reader.readAsBinaryString(file);
  });
}
